import * as React from 'react';
import {useTranslation} from "react-i18next";

import {DataGrid, GridRenderCellParams, GridRowParams, GridToolbarQuickFilter} from "@mui/x-data-grid";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import {OAuthContext} from "../../../context/OAuthContext";
import {formatDateToLocale} from "../utils";
import {toRem} from "../../../themes/utils";
import FetchHelper from "../../Utils/helper";
import Grid from "@mui/material/Grid";
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

interface BlacklistItem {
    id: number;
    email: string;
    createdAt: string;
    salesPartner: object;
}

const CustomToolbar = ({title}: { title: string }) => {
    return <Grid container sx={{p: toRem(16)}} justifyContent="space-between" alignItems="center">
        <Grid item>
            <Typography variant="h3">{title}</Typography>
        </Grid>
        <Grid item>
            <GridToolbarQuickFilter/>
        </Grid>
    </Grid>
}

const BlacklistTable = () => {
    const {oauthData} = React.useContext(OAuthContext);
    const [loading, setLoading] = React.useState(false);
    const [blacklist, setBlacklist] = React.useState<Array<BlacklistItem>>([]);
    const {t} = useTranslation('common');

    React.useEffect(
        () => {
            if (!oauthData.token?.accessToken)
                return;
            const helper = new FetchHelper(oauthData.token);
            setLoading(true);
            helper.getBlacklist().then(res => {
                setBlacklist(res);
                setLoading(false);
            });
        }, [oauthData.token, setBlacklist, setLoading]
    );

    const deleteBlacklistItem = (id: number) => {
        if(!window.confirm(t("confirm-delete")))
            return;

        const helper = new FetchHelper(oauthData.token);

        helper.deleteBlacklistItem(id).then(() => {
            setBlacklist(prevState => prevState.filter(item => item.id !== id));
        });
    };

    const getSalesPartnerName = (params: GridRenderCellParams) => {
        return `${params.row.salesPartner.name}`;
    }

    const getEmail = (params: GridRenderCellParams) => {
        return `${params.row.email}`;
    }

    const formatDate = (params: GridRenderCellParams) => {
        return formatDateToLocale(params.row.createdAt, oauthData)
    }

    const columns = [
        {field: "email", headerName: t("email"), valueGetter: getEmail, flex: 1},
        {field: "salesPartner", headerName: t("sponsor"), valueGetter: getSalesPartnerName, flex: 1},
        {field: "createdAt", headerName: t("created_on"), valueGetter: formatDate, flex: 1},
        {
            field: "deleteButton", type: "actions", getActions: (params: GridRowParams) => [
                <IconButton onClick={() => deleteBlacklistItem(params.row.id)}><DeleteIcon/></IconButton>,
            ]
        },
    ]

    return (
        <Box sx={{width: 'auto'}}>
            {blacklist && <DataGrid autoHeight
                                    loading={loading}
                                    components={{Toolbar: () => CustomToolbar({title: "Blacklist"})}}
                                    componentsProps={{
                                        pagination: {labelRowsPerPage: t("rows_per_page")},
                                    }}
                                    rows={blacklist}
                                    columns={columns}
                                    pagination
                                    localeText={{noRowsLabel: t("no_data")}}
            />}
        </Box>
    );
};

export default BlacklistTable;